import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Briefcase, Building2, EyeOff, Lock, LockOpen, Scale, ScrollText } from 'lucide-react';
import { useInView, usePageVisible } from '../mock/motion/useInView';
import { useReducedMotion } from '../mock/motion/useReducedMotion';
import { isPrerender } from '../../lib/prerender';

// The home hero's picture: how a deal room is shared. One room at its own
// address, each party with its own access, two users kept apart, and every
// action on the record. A diagram drawn on the navy band, not a product mock.
// Copy comes from content/home.js (hero.diagram).
//
// Parties sit four across when the diagram has the width (phones' landscape,
// tablets, xl) and two by two in the lg halves and on phones, so the two
// users come last and share a row either way. The connectors are one SVG
// measured from the tiles: a rail across the parties four across, a spine
// down the column gap two by two.
//
// Motion, once on first view: the room settles and locks, its lines draw out
// to each party, each tile lands as its line reaches it, the bracket closes
// under the users, and the log rises. Then, while it's on screen, a pulse runs
// from the room to one party at a time and each arrival adds a tick to the
// log. Reduced motion shows the final frame, still.

const ICONS = { owner: Building2, guest: Scale, viewer: Briefcase };

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'; // ease-smooth
const DRAW_SPEED = 0.6; // px per ms as the lines draw out
const FLY = 1500; // one pulse, room to party
const GAP = 2600; // between pulses
const ORDER = [2, 0, 3, 1]; // which party each pulse visits, in turn
const TAIL = [64, 30, 12]; // the pulse's layers: glow, tail, bright head
const TICK_HEIGHTS = [6, 10, 7, 13, 8, 4, 11, 9, 14, 6, 9, 5, 12, 7, 10];
const HISTORY = 40;

// A connector from (x0, y0) down to y, across to x1, down to y1, with soft
// corners.
const elbow = (x0, y0, y, x1, y1) => {
  const dx = x1 - x0;
  if (Math.abs(dx) < 1) return `M${x0} ${y0}V${y1}`;
  const s = Math.sign(dx);
  const r = Math.min(8, Math.abs(dx) / 2, y - y0, y1 - y);
  return `M${x0} ${y0}V${y - r}Q${x0} ${y} ${x0 + s * r} ${y}H${x1 - s * r}Q${x1} ${y} ${x1} ${y + r}V${y1}`;
};

// Layout boxes ignore transforms, so tiles still rising measure where they land.
const box = (el) => ({ x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight });

const measure = (root, room, cards, apart) => {
  if (!root || !room || !apart || cards.some((c) => !c)) return null;
  const r = box(room);
  const c = cards.map(box);
  const x0 = r.x + r.w / 2;
  const y0 = r.y + r.h;
  const firstTop = Math.min(...c.map((b) => b.y));
  const firstBottom = Math.max(...c.filter((b) => b.y === firstTop).map((b) => b.y + b.h));
  const rail = y0 + (firstTop - y0) / 2;
  const lines = c.map((b) => {
    // Second row (two by two): the spine runs down the column gap first.
    const y = b.y === firstTop ? rail : firstBottom + (b.y - firstBottom) / 2;
    return elbow(x0, y0, y, b.x + b.w / 2, b.y);
  });
  // The bracket under the two users, drawn from its middle outward.
  const [a, b] = c.slice(-2);
  const ax = a.x + a.w / 2;
  const bx = b.x + b.w / 2;
  const mid = (ax + bx) / 2;
  const top = box(apart).y - 16;
  const bottom = top + 8;
  const bracket = [
    `M${mid} ${bottom}H${ax + 3}Q${ax} ${bottom} ${ax} ${bottom - 3}V${top}`,
    `M${mid} ${bottom}H${bx - 3}Q${bx} ${bottom} ${bx} ${bottom - 3}V${top}`,
  ];
  return { w: root.offsetWidth, h: root.offsetHeight, lines, bracket };
};

// Draws a path from its start, as if the pen were moving.
const draw = (el, duration, delay = 0) => {
  if (!el) return;
  const len = el.getTotalLength();
  el.animate(
    [
      { strokeDasharray: `${len} ${len}`, strokeDashoffset: len },
      { strokeDasharray: `${len} ${len}`, strokeDashoffset: 0 },
    ],
    { duration, delay, easing: EASE, fill: 'backwards' },
  );
};

// One pulse along a party's line: three stacked dashes that share a head, so
// it reads as a light with a fading tail. It runs on into the tile and out.
const fly = (group) => {
  if (!group) return;
  const len = group.firstChild.getTotalLength();
  Array.from(group.children).forEach((path, i) => {
    const dash = TAIL[i];
    path.style.strokeDasharray = `${dash} ${len + 2 * TAIL[0]}`;
    path.animate(
      [
        { strokeDashoffset: dash, opacity: 0 },
        { opacity: 1, offset: 0.15 },
        { opacity: 1, offset: 0.85 },
        { strokeDashoffset: dash - len - TAIL[0], opacity: 0 },
      ],
      { duration: FLY, easing: 'cubic-bezier(0.45, 0, 0.25, 1)' },
    );
  });
};

const hidden = 'translate-y-2 opacity-0';
const shown = 'translate-y-0 opacity-100';
const rise = { transitionProperty: 'opacity, transform', transitionDuration: '600ms', transitionTimingFunction: EASE };

// One bar in the log's strip. New ones grow in at the right and arrive lit.
const Tick = ({ height, fresh, delay }) => {
  const ref = useRef(null);
  const [lit, setLit] = useState(fresh);
  useLayoutEffect(() => {
    const el = ref.current;
    if (fresh) {
      el.animate([{ width: '0px', opacity: 0 }, { width: '6px', opacity: 1 }], { duration: 500, easing: EASE });
      const timer = setTimeout(() => setLit(false), 900);
      return () => clearTimeout(timer);
    }
    if (delay != null) {
      el.firstChild.animate([{ transform: 'scaleY(0)' }, { transform: 'scaleY(1)' }], { duration: 500, delay, easing: EASE, fill: 'backwards' });
    }
    return undefined;
  }, [fresh, delay]);
  return (
    <span ref={ref} className="flex h-full w-1.5 flex-none items-end justify-end overflow-hidden">
      <span
        className={`block w-[3px] origin-bottom rounded-sm transition-colors duration-1000 ${lit ? 'bg-progress-check-dark' : 'bg-primary-500'}`}
        style={{ height }}
      />
    </span>
  );
};

const Party = ({ party, cardRef, visible, active }) => {
  const Icon = ICONS[party.icon];
  return (
    <div
      ref={cardRef}
      className={`flex flex-col rounded-md border bg-primary-800 p-4 ${visible ? shown : hidden} ${active ? 'border-primary-500' : 'border-primary-700'}`}
      style={{ ...rise, transitionProperty: 'opacity, transform, border-color' }}
    >
      <span className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-500 ${active ? 'bg-primary-600' : 'bg-primary-700'}`}>
        <Icon className="h-4 w-4 text-primary-200" aria-hidden="true" />
      </span>
      <div className="mt-3 text-sm font-semibold text-white">{party.name}</div>
      <div className="text-xs text-primary-300">{party.role}</div>
      <div className="mt-4 flex items-center gap-2 border-t border-primary-700 pt-3 text-xs font-medium text-primary-100">
        <span
          className={`h-1.5 w-1.5 flex-shrink-0 rounded-full transition-[background-color,box-shadow] duration-500 ${
            active ? 'bg-progress-check-dark shadow-[0_0_0_3px_rgba(107,213,163,0.18)]' : 'bg-primary-500'
          }`}
        />
        {party.access}
      </div>
    </div>
  );
};

export const DealDiagram = ({ room, parties, apart, log }) => {
  const rootRef = useRef(null);
  const roomRef = useRef(null);
  const apartRef = useRef(null);
  const cardRefs = useRef([]);
  const lineRefs = useRef([]);
  const bracketRefs = useRef([]);
  const pulseRefs = useRef([]);

  const reduced = useReducedMotion();
  const inView = useInView(rootRef, 0.35);
  const pageVisible = usePageVisible();

  const [geo, setGeo] = useState(null);
  const [step, setStep] = useState({ room: false, locked: false, lines: false, cards: parties.map(() => false), bracket: false, log: false, live: false });
  const [active, setActive] = useState(null);
  const [ticks, setTicks] = useState(() => Array.from({ length: HISTORY }, (_, i) => ({ id: i, height: TICK_HEIGHTS[i % TICK_HEIGHTS.length] })));
  const intro = useRef('waiting'); // waiting → playing → done
  const nextId = useRef(HISTORY);
  const turn = useRef(0);

  // Measure the connectors now and whenever the layout changes. Prerendered
  // HTML has none: they're drawn for the visitor's width.
  useLayoutEffect(() => {
    if (isPrerender) return undefined;
    const update = () => {
      const next = measure(rootRef.current, roomRef.current, cardRefs.current, apartRef.current);
      setGeo((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, [parties.length]);

  // Reduced motion: the final frame.
  useEffect(() => {
    if (!reduced) return;
    intro.current = 'done';
    setStep((s) => ({ ...s, room: true, locked: true, lines: true, cards: s.cards.map(() => true), bracket: true, log: true }));
  }, [reduced]);

  const measured = geo !== null;

  // The intro, once, the first time the diagram is on screen.
  useEffect(() => {
    if (reduced || !inView || !measured || intro.current !== 'waiting') return undefined;
    intro.current = 'playing';
    const timers = [];
    const at = (ms, fn) => timers.push(setTimeout(fn, ms));
    const set = (patch) => setStep((s) => ({ ...s, ...patch }));

    const linesAt = 380;
    const lengths = lineRefs.current.map((el) => (el ? el.getTotalLength() : 0));
    const durations = lengths.map((len) => Math.max(500, len / DRAW_SPEED));
    const arrivals = durations.map((d) => linesAt + d * 0.62);
    const bracketAt = Math.max(...arrivals) + 260;

    set({ room: true });
    at(460, () => set({ locked: true }));
    at(linesAt, () => {
      lineRefs.current.forEach((el, i) => draw(el, durations[i]));
      set({ lines: true });
    });
    arrivals.forEach((t, i) => at(t, () => setStep((s) => ({ ...s, cards: s.cards.map((v, j) => v || j === i) }))));
    at(bracketAt, () => {
      bracketRefs.current.forEach((el) => draw(el, 420));
      set({ bracket: true });
    });
    at(bracketAt + 280, () => set({ log: true }));
    at(bracketAt + 1500, () => {
      intro.current = 'done';
      set({ live: true });
    });
    return () => {
      timers.forEach(clearTimeout);
      // Unmounted mid-intro (or StrictMode's rehearsal): start over next time.
      if (intro.current === 'playing') intro.current = 'waiting';
    };
  }, [reduced, inView, measured]);

  const pulse = useCallback(() => {
    const i = ORDER[turn.current % ORDER.length] % parties.length;
    turn.current += 1;
    fly(pulseRefs.current[i]);
    const arrive = setTimeout(() => {
      setActive(i);
      const id = nextId.current;
      nextId.current += 1;
      setTicks((list) => [...list.slice(-(HISTORY - 1)), { id, height: TICK_HEIGHTS[id % TICK_HEIGHTS.length], fresh: true }]);
    }, FLY * 0.6);
    const settle = setTimeout(() => setActive(null), FLY * 0.6 + 1100);
    return [arrive, settle];
  }, [parties.length]);

  // Then, while it's on screen: a pulse to one party at a time.
  useEffect(() => {
    if (reduced || !step.live || !inView || !pageVisible || !measured) return undefined;
    let timers = pulse();
    const loop = setInterval(() => {
      timers = pulse();
    }, GAP);
    return () => {
      clearInterval(loop);
      timers.forEach(clearTimeout);
      setActive(null);
    };
  }, [reduced, step.live, inView, pageVisible, measured, pulse]);

  const label = `${room.name}: ${parties.map((p) => `${p.name}, ${p.access}`).join('; ')}. ${apart}. ${log.title}: ${log.text}.`;
  const historyDelay = (i) => (step.log && !reduced && intro.current !== 'done' ? 200 + i * 14 : null);

  return (
    <figure className="select-none" aria-label={label}>
      <div ref={rootRef} className="relative" aria-hidden="true">
        {geo && (
          <svg className="pointer-events-none absolute inset-0 overflow-visible" width={geo.w} height={geo.h} viewBox={`0 0 ${geo.w} ${geo.h}`} fill="none">
            <defs>
              <filter id="deal-diagram-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2.5" />
              </filter>
            </defs>
            <g className="stroke-primary-600" strokeWidth="1">
              {geo.lines.map((d, i) => (
                <path key={i} ref={(el) => (lineRefs.current[i] = el)} d={d} style={step.lines ? undefined : { strokeDasharray: '0 1000000' }} />
              ))}
              {geo.bracket.map((d, i) => (
                <path key={i} ref={(el) => (bracketRefs.current[i] = el)} d={d} style={step.bracket ? undefined : { strokeDasharray: '0 1000000' }} />
              ))}
            </g>
            {geo.lines.map((d, i) => (
              <g key={i} ref={(el) => (pulseRefs.current[i] = el)} strokeLinecap="round">
                <path d={d} className="stroke-primary-200" strokeWidth="6" opacity="0" style={{ strokeOpacity: 0.35 }} filter="url(#deal-diagram-glow)" />
                <path d={d} className="stroke-primary-200" strokeWidth="2" opacity="0" style={{ strokeOpacity: 0.45 }} />
                <path d={d} className="stroke-white" strokeWidth="1.5" opacity="0" />
              </g>
            ))}
          </svg>
        )}

        {/* The room */}
        <div
          ref={roomRef}
          className={`mx-auto flex w-fit items-center gap-3 rounded-md border border-primary-600 bg-primary-800 px-5 py-4 shadow-frame-navy ${step.room ? shown : hidden}`}
          style={rise}
        >
          <span className="relative flex h-9 w-9 items-center justify-center rounded bg-white">
            <LockOpen
              className={`absolute h-4 w-4 text-primary-900 transition-[opacity,transform] duration-300 ${step.locked ? '-translate-y-0.5 opacity-0' : 'opacity-100'}`}
              style={{ transitionTimingFunction: EASE }}
            />
            <Lock
              className={`absolute h-4 w-4 text-primary-900 transition-[opacity,transform] duration-300 ${step.locked ? 'opacity-100' : 'translate-y-0.5 opacity-0'}`}
              style={{ transitionTimingFunction: EASE }}
            />
          </span>
          <div>
            <div className="text-sm font-semibold text-white">{room.name}</div>
            <div className="text-xs tabular-nums text-primary-300">{room.address}</div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4">
          {parties.map((party, i) => (
            <Party key={party.name} party={party} cardRef={(el) => (cardRefs.current[i] = el)} visible={step.cards[i]} active={active === i} />
          ))}

          {/* Under the two users, below the bracket the SVG draws */}
          <div
            ref={apartRef}
            className={`col-span-2 flex items-center justify-center gap-1.5 text-xs font-medium text-primary-200 transition-opacity duration-500 sm:col-start-3 lg:col-start-1 xl:col-start-3 ${
              step.bracket ? 'opacity-100 delay-200' : 'opacity-0'
            }`}
          >
            <EyeOff className="h-3.5 w-3.5 flex-shrink-0" />
            {apart}
          </div>
        </div>

        {/* The record */}
        <div
          className={`mt-6 flex items-center gap-3 rounded-md border border-primary-700 bg-primary-800 px-4 py-3.5 ${step.log ? shown : hidden}`}
          style={rise}
        >
          <ScrollText className="h-4 w-4 flex-shrink-0 text-primary-200" />
          <div className="min-w-0 flex-1 sm:flex-none">
            <div className="text-sm font-semibold text-white">{log.title}</div>
            <div className="text-xs text-primary-300">{log.text}</div>
          </div>
          <div className="hidden h-4 min-w-0 flex-1 items-end justify-end overflow-hidden sm:flex">
            {ticks.map((t, i) => (
              <Tick key={t.id} height={t.height} fresh={!!t.fresh} delay={t.fresh ? null : historyDelay(i)} />
            ))}
          </div>
          <span className="flex-shrink-0 rounded border border-primary-600 px-2 py-0.5 text-xs font-medium text-primary-100">{log.badge}</span>
        </div>
      </div>
    </figure>
  );
};
